/**
 * Single seam between Keycloak and the rest of the app. Module-scope
 * singleton — one adapter, one session, one set of timers per page load.
 *
 * A 401 re-authenticates, so the retry budget lives in sessionStorage: every
 * attempt is a full page navigation, and a counter in module scope would reset
 * itself on the way back and never notice the bounce repeating.
 *
 * Related state lives elsewhere by design:
 *   tokenRefresh.ts   — scheduled refresh + visibility-resume listener
 *   loaderGuards.ts   — requireAuth + intended-URL stash/pop protocol
 *   stores/authStore  — reactive {isAuthenticated, error} for React
 *
 * Invariants:
 *   - initialize() must resolve before <App> mounts (loaders rely on it).
 *   - logout / identity switch evicts the React Query cache — it holds
 *     tenant-scoped data that must not survive a user change.
 */

import { toast } from 'sonner';

import { isEmbedded } from '@/features/validate/services/embed/embed';
import {
  awaitEmbedToken,
  getEmbedToken,
  onEmbedToken,
} from '@/features/validate/services/embed/embedAuth';
import {
  postAuthFailed,
  postAuthRecovered,
  postError,
} from '@/features/validate/services/embed/embedBridge';
import keycloak from '@/platform/auth/services/keycloak';
import { popIntended, stashIntended } from '@/platform/auth/services/loaderGuards';
import {
  startRefreshLifecycle,
  refreshNow,
  stopRefresh,
} from '@/platform/auth/services/tokenRefresh';
import { useAuthStore } from '@/platform/auth/stores/authStore';
import { isProblemDocument, ProblemSlug } from '@/shared/api/errors/errors';
import { queryClient } from '@/shared/api/queryClient';

import type { AxiosError } from 'axios';

const REAUTH_KEY = 'kc-reauth-count';
const REAUTH_MAX = 3;

// Two asks, matched to the host: Validate refreshes on the first `auth-failed`, then leaves
// recovery to the user. Only a 2xx clears the budget, and a fresh frame is a fresh chance.
const EMBED_ASK_MAX = 2;
let embedAsksSpent = 0;
let embedAuthUnavailable = false;
let embedTerminalReported = false;
let embedRejectedToken: string | null = null;
let embedRecoveryWired = false;
let embedTokenSabotage = false;

const attemptsSpent = (): number => Number(sessionStorage.getItem(REAUTH_KEY) || '0');

/** False once the budget is spent — the caller must not redirect again. */
const takeReauthAttempt = (): boolean => {
  const next = attemptsSpent() + 1;
  if (next > REAUTH_MAX) return false;
  sessionStorage.setItem(REAUTH_KEY, String(next));
  return true;
};

const clearReauthAttempts = () => {
  if (attemptsSpent()) sessionStorage.removeItem(REAUTH_KEY);
};

const cleanKcCallbacks = () => {
  const removeMatching = (storage: Storage) => {
    const toRemove: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key?.startsWith('kc-callback-')) toRemove.push(key);
    }
    toRemove.forEach((key) => storage.removeItem(key));
  };
  removeMatching(sessionStorage);
  removeMatching(localStorage);
};

let initInProgress = false;

const actions = () => useAuthStore.getState();

// Re-authenticating would mint the same rejected token, so stop and fall back to
// the Login page, whose "Try Again" restores the budget via login().
const reportRejectedSession = () => {
  stopRefresh();
  queryClient.clear();
  actions().setAuthenticated(false);
  actions().setLoading(false);
  actions().setError(
    'You signed in, but the server rejected the session. This usually means the app and the API are configured for different identity providers. Click "Try Again" to retry.'
  );
};

// Terminal only in that the child stops asking; the host can still rescue it, so the cache stays.
const reportHostAuthUnavailable = () => {
  embedAuthUnavailable = true;
  embedAsksSpent = EMBED_ASK_MAX;
  embedRejectedToken = getEmbedToken();
  stopRefresh();
  actions().setAuthenticated(false);
  actions().setLoading(false);
  actions().setError(
    'Hazcheck Validate could not sign you in to Extract. Anything you have curated is still here.'
  );

  // Once per episode: the host refreshes on `auth-failed`, so re-announcing restarts the ping-pong.
  if (embedTerminalReported) return;
  embedTerminalReported = true;
  // `error` first, so a host reading codes sees the terminal one, not another retry cue.
  postError('auth-unavailable', 'The host did not supply a usable auth token.');
  postAuthFailed();
};

const restoreEmbedAuth = () => {
  if (!embedAuthUnavailable) return;
  embedAuthUnavailable = false;
  actions().setError(null);
  actions().setAuthenticated(true);
  actions().setLoading(false);
};

const clearEmbedAuthFailure = () => {
  // Every ask the host answered, not just the terminal one: it has nothing else to tell recovery from a broken frame.
  const wasAnnounced = embedAsksSpent > 0 || embedTerminalReported;
  embedAsksSpent = 0;
  embedRejectedToken = null;
  embedTerminalReported = false;

  if (wasAnnounced) postAuthRecovered();

  restoreEmbedAuth();
};

// A different token gets one chance at the 2xx that clears the failure, but the budget stays spent.
const admitFreshToken = (token: string) => {
  if (token === embedRejectedToken) return;
  restoreEmbedAuth();
};

const wireEmbedRecovery = () => {
  if (embedRecoveryWired) return;
  embedRecoveryWired = true;
  onEmbedToken(admitFreshToken);
};

const failures = (() => {
  let pending = false;
  return {
    trip: () => {
      // Embedded: never redirect to Keycloak — it would navigate the iframe and risk a loop.
      if (isEmbedded()) {
        if (embedAuthUnavailable || pending) return;
        pending = true;
        setTimeout(() => {
          pending = false;
          embedAsksSpent += 1;
          if (embedAsksSpent > EMBED_ASK_MAX) reportHostAuthUnavailable();
          else postAuthFailed();
        }, 100);
        return;
      }
      if (initInProgress || pending) return;
      pending = true;
      // Debounced so a burst of parallel 401s costs one attempt, not one each.
      setTimeout(() => {
        pending = false;
        if (takeReauthAttempt()) {
          // Keycloak returns to the origin, so the current URL has to be captured
          // here or the round-trip lands the user on / instead of where they were.
          stashIntended(window.location.pathname + window.location.search);
          keycloak.login({ redirectUri: window.location.origin });
        } else reportRejectedSession();
      }, 100);
    },
    reset: () => {
      clearEmbedAuthFailure();
      clearReauthAttempts();
    },
  };
})();

const hasOAuthParams = (): boolean => {
  const urlParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.substring(1));
  const code = urlParams.get('code') || hashParams.get('code');
  const state = urlParams.get('state') || hashParams.get('state');
  const sessionState = urlParams.get('session_state') || hashParams.get('session_state');
  return !!(code || state || sessionState);
};

const cleanOAuthParams = () => {
  const cleanParams = new URLSearchParams(window.location.search);
  cleanParams.delete('code');
  cleanParams.delete('state');
  cleanParams.delete('session_state');
  const search = cleanParams.toString();
  const cleanUrl = window.location.origin + window.location.pathname + (search ? `?${search}` : '');
  window.history.replaceState({}, document.title, cleanUrl);
};

async function initialize() {
  // Embedded mode: the parent (Validate) supplies the auth token via the embed
  // bridge. Skip Keycloak entirely — no redirect, no check-sso iframe — and
  // wait for the injected token before the profile prefetch (app/main.tsx) runs.
  if (isEmbedded()) {
    initInProgress = true;
    wireEmbedRecovery();
    try {
      const token = await awaitEmbedToken();
      if (!token) {
        reportHostAuthUnavailable();
        return;
      }
      actions().setAuthenticated(true);
      actions().setLoading(false);
    } finally {
      initInProgress = false;
    }
    return;
  }

  initInProgress = true;
  actions().setError(null);

  try {
    const hasOAuth = hasOAuthParams();

    const authenticated = await keycloak.init({
      onLoad: 'check-sso',
      checkLoginIframe: false,
      silentCheckSsoFallback: false,
      pkceMethod: 'S256',
      flow: 'standard',
      enableLogging: false,
    });

    cleanKcCallbacks();

    if (authenticated && hasOAuth) {
      cleanOAuthParams();
      // replaceState (not navigate) so React Router matches the intended path
      // on its first render rather than landing on / then bouncing.
      const intended = popIntended();
      if (intended) window.history.replaceState(null, '', intended);
    }

    actions().setAuthenticated(authenticated);

    if (authenticated && keycloak.token) {
      startRefreshLifecycle(logout);
    } else {
      actions().clearAuth();
      queryClient.clear();
    }

    actions().setLoading(false);
  } catch (error) {
    console.error('Authentication initialization failed:', error);
    actions().setAuthenticated(false);
    actions().setLoading(false);
    actions().setError(error instanceof Error ? error.message : 'Authentication failed');
  } finally {
    initInProgress = false;
  }
}

function login() {
  // Embedded: the parent owns auth — never redirect the iframe to Keycloak.
  if (isEmbedded()) {
    clearEmbedAuthFailure();
    postAuthFailed();
    return;
  }
  clearReauthAttempts();
  cleanKcCallbacks();
  actions().setError(null);
  keycloak.login({ redirectUri: window.location.origin });
}

let signOutResets: readonly (() => void)[] = [];

export function setSignOutResets(resets: readonly (() => void)[]): void {
  signOutResets = resets;
}

function logout() {
  stopRefresh();
  clearReauthAttempts();
  actions().clearAuth();
  // Evict the entire cache so a re-auth as a different identity can't read
  // prior-tenant data before its own refetches land.
  queryClient.clear();
  signOutResets.forEach((reset) => reset());
  keycloak.logout({ redirectUri: window.location.origin });
}

async function getValidToken(): Promise<string | null> {
  if (isEmbedded()) {
    const token = getEmbedToken();
    // Dev only: a working Validate can't be asked for a bad token, so corrupt it here. The
    // `import.meta.env.DEV` guard is what lets the bundler drop this from a production build.
    if (import.meta.env.DEV && embedTokenSabotage && token) return `${token}-sabotaged`;
    if (token) return token;
    // Already given up on the host — don't spend another timeout per request.
    if (embedAuthUnavailable) return null;
    return await awaitEmbedToken();
  }
  if (initInProgress) return keycloak.token || null;
  if (!keycloak.authenticated || !keycloak.token) return keycloak.token || null;

  try {
    await refreshNow(5);
    return keycloak.token || null;
  } catch {
    return null;
  }
}

// A 2xx from the Extract API is the only proof its 401s have stopped — the one
// restore point on the request path.
function notifyRequestSucceeded() {
  failures.reset();
}

function handleApiAuthError(error: AxiosError) {
  // Hazcheck rejecting our own service account arrives as a 401/403 carrying this
  // slug. The user's session is fine, so re-authenticating would spend the budget
  // and end on a message blaming identity-provider config for an upstream outage.
  const problem = error.response?.data;
  if (isProblemDocument(problem) && problem.type === ProblemSlug.HazcheckValidationFailed) return;

  if (error.response?.status === 401) {
    failures.trip();
  } else if (error.response?.status === 403) {
    toast.error('Access Denied', {
      description: "You don't have permission to perform this action.",
    });
  }
}

function embedAuthDiagnostics() {
  return {
    asksSpent: embedAsksSpent,
    askMax: EMBED_ASK_MAX,
    unavailable: embedAuthUnavailable,
    sabotaged: embedTokenSabotage,
  };
}

function devSetEmbedTokenSabotage(on: boolean) {
  if (!import.meta.env.DEV) return;
  embedTokenSabotage = on;
}

export const authService = {
  initialize,
  login,
  logout,
  getValidToken,
  notifyRequestSucceeded,
  handleApiAuthError,
  embedAuthDiagnostics,
  devSetEmbedTokenSabotage,
};
